import classNames from 'classnames';
import { HTMLAttributes, PropsWithChildren, useMemo } from 'react';
import { IconBaseProps, IconType } from 'react-icons';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { VariantProps } from 'tailwind-variants';
import { IsVisible, Tooltip } from '../../../utils';
import { fieldBaseStyles } from './styles';

export interface IFieldBaseProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof fieldBaseStyles.container> {
  error?: string;
  hidden?: boolean;
  icon?: IconType;
  iconProps?: IconBaseProps;
  label?: string;
  labelProps?: {
    className?: string;
    rest?: HTMLAttributes<HTMLLabelElement>;
    variants?: VariantProps<typeof fieldBaseStyles.label>;
  };
  name?: string;
  tooltipMessage?: string;
}

export function FieldBase({
  children,
  className,
  design,
  disabled,
  error,
  hidden,
  icon: Icon,
  iconPosition,
  iconProps,
  label,
  labelProps,
  name,
  size,
  tooltipMessage,
  ...rest
}: PropsWithChildren<IFieldBaseProps>): JSX.Element {
  const iconSize = useMemo(() => {
    let iconSizeValue = 20;
    if (size === 'lg') {
      iconSizeValue = 22;
    } else if (size === 'sm') {
      iconSizeValue = 18;
    }

    return iconSizeValue;
  }, [size]);

  return (
    <div className={classNames('max-w-full w-full relative', { hidden })}>
      <IsVisible when={!!label}>
        <label
          className={fieldBaseStyles.label({
            className: labelProps?.className,
            design: labelProps?.variants?.design,
            errored: !!error,
          })}
          htmlFor={name}
          {...labelProps?.rest}
        >
          {label}
          <IsVisible when={!!tooltipMessage}>
            <Tooltip message={tooltipMessage}>
              <HiOutlineInformationCircle size={20} />
            </Tooltip>
          </IsVisible>
        </label>
      </IsVisible>
      <div
        className={fieldBaseStyles.container({
          design,
          size,
          iconPosition: Icon && iconPosition,
          disabled,
          className,
          errored: !!error,
        })}
        {...rest}
      >
        {Icon && (
          <div className="flex items-center justify-center w-fit">
            <Icon
              className={fieldBaseStyles.icon({
                className: iconProps?.className,
                errored: !!error,
              })}
              size={iconProps?.size || iconSize}
              {...iconProps}
            />
          </div>
        )}
        {children}
      </div>
      <IsVisible when={!!error}>
        <div className="flex mt-1 ml-auto w-fit text-right">
          <span className="text-primary-two text-xs text-right">{error}</span>
        </div>
      </IsVisible>
    </div>
  );
}
